import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { type CompanyProfileResponse, listDataSourcesOptions } from "@/api";
import { DefinitionList, PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { profileToForm } from "./companyForm";
import { useCompanyLogoUrl } from "./useCompanyLogo";

/** Phones: the profile's values, no editing (docs/design/web/WEB_ADMIN_DESIGN.md decision 2). */
export function CompanyProfileReadOnly({ profile }: Readonly<{ profile: CompanyProfileResponse | null }>) {
  const { t } = useTranslation("adminCompany");
  const sources = useQuery({ ...listDataSourcesOptions({ query: { page_size: 100 } }), enabled: Boolean(profile) });
  const logoUrl = useCompanyLogoUrl(Boolean(profile?.logo_url));
  if (!profile) {
    return (
      <Text variant="body-sm" tone="tertiary">
        {t("empty")}
      </Text>
    );
  }
  const v = profileToForm(profile);
  const dash = t("notSet");
  const show = (value: string) => value.trim() || dash;
  const kb = v.knowledgeBase
    ? ((sources.data?.data ?? []).find((s) => s.uuid === v.knowledgeBase)?.name ?? v.knowledgeBase)
    : t("knowledge.none");

  return (
    <div className="flex flex-col gap-8">
      <PageSection title={t("identity.title")}>
        <DefinitionList
          items={[
            { label: t("fields.companyName"), value: show(v.companyName) },
            { label: t("fields.industry"), value: show(v.industry) },
            { label: t("fields.website"), value: show(v.website) },
            { label: t("fields.description"), value: show(v.description) },
            { label: t("fields.mission"), value: show(v.mission) },
            { label: t("fields.values"), value: show(v.values) },
            { label: t("fields.foundedYear"), value: show(v.foundedYear) },
            { label: t("fields.headquarters"), value: show(v.headquarters) },
            { label: t("fields.employeeCount"), value: show(v.employeeCount) },
            { label: t("fields.productsServices"), value: show(v.productsServices) },
          ]}
        />
      </PageSection>
      <PageSection title={t("leadership.title")}>
        <DefinitionList
          items={
            v.leadership.length > 0
              ? v.leadership.map((row, i) => ({
                  label: t("leadership.entry", { n: i + 1, name: row.name }),
                  value: show(row.title),
                }))
              : [{ label: t("leadership.title"), value: t("leadership.empty") }]
          }
        />
      </PageSection>
      <PageSection title={t("terminology.title")}>
        <Text variant="body-sm">{show(v.keyTerminology)}</Text>
      </PageSection>
      <PageSection title={t("branding.title")}>
        {logoUrl && <img src={logoUrl} alt={t("logo.alt")} className="h-16 max-w-full self-start object-contain" />}
        <DefinitionList
          items={[
            { label: t("branding.primary"), value: show(v.primary) },
            { label: t("branding.secondary"), value: show(v.secondary) },
            { label: t("branding.background"), value: show(v.background) },
            { label: t("branding.text"), value: show(v.textColor) },
            { label: t("branding.accents"), value: show(v.accents) },
            { label: t("branding.headingFont"), value: show(v.headingFont) },
            { label: t("branding.bodyFont"), value: show(v.bodyFont) },
            { label: t("branding.tone"), value: show(v.tone) },
            { label: t("branding.guidance"), value: show(v.brandStyle) },
          ]}
        />
      </PageSection>
      <PageSection title={t("knowledge.title")}>
        <DefinitionList items={[{ label: t("fields.knowledgeBase"), value: kb }]} />
      </PageSection>
    </div>
  );
}
